import { NavigationMenuConfig } from "@/components/layout/NavigationMenu/config";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { IconDefinition } from "@icons/duotone";
import { Button, Icon } from "@ui";
import { Link } from "gatsby";
import * as React from "react";
import { Drawer } from "vaul";

type NavigationMenuItem = {
  title: string;
  icon?: IconDefinition;
  description: string;
  href: string;
};

type BottomNavItem = {
  title?: string;
  path?: string;
  isExternal?: boolean;
  onClick?: () => void;
  id?: string;
  className?: string;
  target?: string;
};

interface NavigationDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bottomNavItems?: BottomNavItem[];
}

const ChevronRightIcon = () => (
  <svg className="tw-h-4 tw-w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" fill="currentColor">
    <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
  </svg>
);

const DrawerMenuItem = ({ item, onClick }: { item: NavigationMenuItem; onClick?: () => void }) => {
  return (
    <Link
      to={item.href}
      onClick={onClick}
      className="tw-flex tw-items-start tw-space-x-3 tw-rounded-lg tw-p-3 tw-no-underline tw-transition-colors hover:tw-bg-black/5 hover:tw-no-underline dark:hover:tw-bg-white/5"
    >
      {item.icon && (
        <div className="tw-mt-1" style={{ color: "var(--foreground-color)", opacity: 0.7 }}>
          <Icon icon={item.icon as IconProp} />
        </div>
      )}
      <div className="tw-flex-1">
        <div className="tw-font-medium" style={{ color: "var(--foreground-color)" }}>
          {item.title}
        </div>
        <div className="tw-text-sm" style={{ color: "var(--foreground-color)", opacity: 0.7 }}>
          {item.description}
        </div>
      </div>
      <ChevronRightIcon />
    </Link>
  );
};

const DrawerSection = ({
  title,
  items,
  gradient,
  onItemClick,
}: {
  title: string;
  items: NavigationMenuItem[];
  gradient?: string;
  onItemClick?: () => void;
}) => {
  return (
    <div className="tw-space-y-2">
      <div className="tw-flex tw-items-center tw-space-x-2 tw-px-3">
        <h3
          className="tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide"
          style={{ color: "var(--foreground-color)", opacity: 0.6 }}
        >
          {title}
        </h3>
        {gradient && <div className="tw-h-0.5 tw-flex-1 tw-rounded-full" style={{ background: gradient }} />}
      </div>
      <div className="tw-space-y-1">
        {items.map(item => (
          <DrawerMenuItem key={item.title} item={item} onClick={onItemClick} />
        ))}
      </div>
    </div>
  );
};

export default function NavigationDrawer({ open, onOpenChange, bottomNavItems = [] }: NavigationDrawerProps) {
  const handleItemClick = () => {
    onOpenChange(false);
  };

  const renderBottomNavButton = (item: BottomNavItem, index: number) => {
    const handleClick = () => {
      if (item.onClick) {
        item.onClick();
      }
      handleItemClick();
    };

    const buttonProps = {
      variant: index === 0 ? ("primary-gradient" as const) : ("secondary-outline" as const),
      size: "md" as const,
      className: "tw-box-border tw-w-full tw-uppercase tw-no-underline hover:tw-no-underline",
      style: index !== 0 ? { border: "1px solid white" } : undefined,
    };

    if (item.onClick) {
      return (
        <Button key={item.id || item.title || index} {...buttonProps} onClick={handleClick}>
          {item.title}
        </Button>
      );
    }

    return (
      <Button key={item.id || item.title || index} {...buttonProps} asChild>
        {item.isExternal ? (
          <a href={item.path} target={item.target || "_blank"}>
            {item.title}
          </a>
        ) : (
          <Link to={item.path}>{item.title}</Link>
        )}
      </Button>
    );
  };

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay className="tw-fixed tw-inset-0 tw-bg-black/40" />
        <Drawer.Content className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-50 tw-mt-24 tw-flex tw-h-[90%] tw-flex-col tw-rounded-t-[10px] tw-bg-[var(--background-color)] tw-text-[var(--foreground-color)]">
          <div className="tw-mx-auto tw-mt-4 tw-h-1 tw-w-12 tw-flex-shrink-0 tw-rounded-full" />

          <div className="tw-flex tw-flex-col tw-overflow-y-auto tw-p-4">
            <div className="tw-space-y-6">
              <div className="tw-space-y-4">
                <h2 className="tw-text-lg tw-font-semibold tw-text-[var(--foreground-color)]">
                  {NavigationMenuConfig.products.title}
                </h2>

                <DrawerSection
                  title={NavigationMenuConfig.products.items.column1.heading}
                  items={NavigationMenuConfig.products.items.column1.items}
                  gradient={NavigationMenuConfig.products.items.column1.gradient}
                  onItemClick={handleItemClick}
                />

                <DrawerSection
                  title={NavigationMenuConfig.products.items.column2.heading}
                  items={NavigationMenuConfig.products.items.column2.items}
                  gradient={NavigationMenuConfig.products.items.column2.gradient}
                  onItemClick={handleItemClick}
                />

                <DrawerSection
                  title={NavigationMenuConfig.products.items.column3.heading}
                  items={NavigationMenuConfig.products.items.column3.items}
                  gradient={NavigationMenuConfig.products.items.column3.gradient}
                  onItemClick={handleItemClick}
                />
              </div>

              <div className="tw-space-y-4 tw-border-t tw-border-[var(--foreground-color-muted)] tw-pt-4">
                <h2 className="tw-text-lg tw-font-semibold tw-text-[var(--foreground-color)]">
                  {NavigationMenuConfig.solutions.title}
                </h2>
                <div className="tw-space-y-1">
                  {NavigationMenuConfig.solutions.items.map(item => (
                    <DrawerMenuItem key={item.title} item={item} onClick={handleItemClick} />
                  ))}
                </div>
              </div>

              <div className="tw-space-y-4 tw-border-t tw-border-[var(--foreground-color-muted)] tw-pt-4">
                <h2 className="tw-text-lg tw-font-semibold tw-text-[var(--foreground-color)]">
                  {NavigationMenuConfig.inspiration.title}
                </h2>
                <div className="tw-space-y-1">
                  {NavigationMenuConfig.inspiration.items.map(item => (
                    <DrawerMenuItem key={item.title} item={item} onClick={handleItemClick} />
                  ))}
                </div>
              </div>

              <div className="tw-space-y-4 tw-border-t tw-border-[var(--foreground-color-muted)] tw-pt-4">
                <h2 className="tw-text-lg tw-font-semibold tw-text-[var(--foreground-color)]">
                  {NavigationMenuConfig.resources.title}
                </h2>
                <div className="tw-space-y-1">
                  {NavigationMenuConfig.resources.items.map(item => (
                    <DrawerMenuItem key={item.title} item={item} onClick={handleItemClick} />
                  ))}
                </div>
              </div>

              <div className="tw-space-y-4 tw-border-t tw-border-[var(--foreground-color-muted)] tw-pt-4">
                <div className="tw-space-y-1">
                  <DrawerMenuItem
                    item={{
                      title: NavigationMenuConfig.enterprise.title,
                      description: "Solutions for large organizations",
                      href: NavigationMenuConfig.enterprise.href,
                    }}
                    onClick={handleItemClick}
                  />
                  <DrawerMenuItem
                    item={{
                      title: NavigationMenuConfig.pricing.title,
                      description: "Find the right plan for your team",
                      href: NavigationMenuConfig.pricing.href,
                    }}
                    onClick={handleItemClick}
                  />
                </div>
              </div>

              <div className="tw-mt-6 tw-space-y-3 tw-border-t tw-border-[var(--foreground-color-muted)] tw-pt-6">
                {bottomNavItems.map((item, index) => renderBottomNavButton(item, index))}
              </div>
            </div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
